import React, { useEffect, useState } from 'react';
import { IInvoiceFullObject } from '../../Interfaces/IInvoice';
import { IDocumentDownloaded } from '../../Interfaces/IDocumentDownloaded';
import { DocumentDownloadedDialog } from '../../Document/DocumentAssistant/DocumentDownloadedDialog';
import { DocumentPreviewDialog } from '../../Document/DocumentAssistant/DocumentPreviewDialog';
import {
  Checkbox,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TableCell,
  TableRow,
  Tooltip,
} from '@mui/material';
import { IDocument } from '../../Interfaces/IDocument';
import { useSidepanelDocumentContext } from '../LegalTransactionEdit';
import { getFetch } from '../../hooks/useFetch';
import {
  Delete,
  Download,
  FindInPage,
  Search,
  VerticalSplit,
} from '@mui/icons-material';
import { IMIMEType } from '../../Interfaces/IMIMEType';
import { State } from '../../redux/mainReducer';
import { useSelector } from 'react-redux';
import { CustomeTextField } from '../../generic/CustomeTextField';
import { ILegalClient } from '../../Interfaces/ILegalClient';
import { ILegalRole } from '../../Interfaces/ILegalRole';
import { ILegalTransactionType } from '../../Interfaces/ILegalTransactionType';

interface IProps {
  invoiceObject: IInvoiceFullObject;
  invoiceObjectArray: IInvoiceFullObject[];
  setInvoiceObjectArray: Function;
  downloadedArray?: IDocumentDownloaded[];
  //
  legalClients: ILegalClient[];
  setLegalClients: Function;
  legalRoles: ILegalRole[];
  legalTransactionTypes: ILegalTransactionType[];
}

const base64Download = (
  localDocumentObject: IDocument,
  mimeTypes: IMIMEType[]
) => {
  const currentMimeType = mimeTypes.find(
    x => x.idMIMEType === localDocumentObject.idMIMEType
  );

  if (localDocumentObject.Data && currentMimeType) {
    const byteCharacters = atob(localDocumentObject.Data);
    // Each character's code point (charCode) will be the value of the byte.
    // We can create an array of byte values by applying this using the .charCodeAt method for each character in the string.
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    //You can convert this array of byte values into a real typed byte array by passing it to the Uint8Array constructor.
    const byteArray = new Uint8Array(byteNumbers);
    // This in turn can be converted to a BLOB by wrapping it in an array and passing it to the Blob constructor.
    const blob = new Blob([byteArray], { type: currentMimeType.MIMEType });
    const blobUrl = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = localDocumentObject.FileName;
    a.click();
  }
};

export const InvoiceRow: React.FC<IProps> = ({
  invoiceObject,
  invoiceObjectArray,
  setInvoiceObjectArray,
  downloadedArray,
  legalClients,
  setLegalClients,
  legalRoles,
  legalTransactionTypes,
}) => {
  const { setSelectedSidepanelDocument } = useSidepanelDocumentContext();
  const mimeTypeArray = useSelector(
    (state: State) => state.mimeTypeArray.storeAcademicTitle
  );
  const [invoice, setInvoice] = useState(invoiceObject);
  const [localDocumentObject, setLocalDocumentObject] = useState<IDocument>({
    idDocument: invoice.idDocument,
    Data: invoice.Data,
    FileName: invoice.FileName,
    Created_at: new Date().toISOString(),
    idMIMEType: invoice.idMIMEType,
    idDocumentDirectory: null,
    idLegalTransaction: null,
    perEmail: null,
    isFromTemplate: null,
  });
  const [isOpenDownloaded, setIsOpenDownloaded] = useState(false);
  const [isOpenPreview, setisOpenPreview] = useState(false);

  useEffect(() => {
    setInvoiceObjectArray([
      ...invoiceObjectArray.map(x =>
        x.idInvoice === invoice.idInvoice ? invoice : x
      ),
    ]);
  }, [invoice]);

  const handleOpenPreview = () => {
    if (!localDocumentObject.Data) {
      getFetch(
        '/document/',
        localDocumentObject.idDocument,
        setLocalDocumentObject
      );
    }
    setisOpenPreview(true);
  };

  const handleRemove = () => {
    setInvoiceObjectArray([
      ...invoiceObjectArray.filter(x => x.idInvoice !== invoice.idInvoice),
    ]);
  };

  const handleDownload = async () => {
    if (localDocumentObject.Data) {
      base64Download(localDocumentObject, mimeTypeArray);
    } else {
      getFetch(
        '/document/',
        localDocumentObject.idDocument,
        (localDocumentObject: IDocument) => {
          setLocalDocumentObject(localDocumentObject);
          base64Download(localDocumentObject, mimeTypeArray);
        }
      );
    }
  };

  return (
    <>
      <DocumentDownloadedDialog
        open={isOpenDownloaded}
        onClose={() => setIsOpenDownloaded(false)}
        localDocumentObject={localDocumentObject}
      />
      <DocumentPreviewDialog
        open={isOpenPreview}
        localDocumentObject={localDocumentObject}
        onClose={() => setisOpenPreview(false)}
      />
      <TableRow>
        <TableCell>
          {invoice.idInvoice < 0 ? <>Neu</> : invoice.idInvoice}
        </TableCell>
        {downloadedArray && (
          <TableCell>
            {
              downloadedArray.filter(
                document =>
                  document.idDocument === localDocumentObject.idDocument
              ).length
            }
          </TableCell>
        )}
        <TableCell>
          <CustomeTextField
            label='Art'
            attr='idLegalTransactionType'
            object={invoice}
            setObject={setInvoice}
            type='select'
          >
            {legalTransactionTypes.map(legalType => (
              <MenuItem
                key={`invoice-legalTransactionTypes-${legalType.idLegalTransactionType}`}
                value={legalType.idLegalTransactionType}
              >
                {legalType.LegalTransactionType}
              </MenuItem>
            ))}
          </CustomeTextField>
        </TableCell>
        <TableCell>
          <FormControl fullWidth>
            <InputLabel id='folder-label' shrink>
              Schuldner
            </InputLabel>
            <Select
              labelId='folder-label'
              placeholder='Schuldner wählen'
              id='schuldner'
              multiple
              notched
              label='Ordner'
              value={invoice.LegalClients}
              onChange={e => {
                const value = e.target.value;

                if (value && typeof value !== 'string') {
                  setInvoice({
                    ...invoice,
                    LegalClients: value,
                  });
                  setLegalClients([
                    ...legalClients.map(legalClient => {
                      return {
                        ...legalClient,
                        enableInvoiceReminding:
                          value.indexOf(legalClient.idLegalClient) > -1,
                      };
                    }),
                  ]);
                }
              }}
              renderValue={selectedValues =>
                selectedValues
                  .map(selectedValue => {
                    const testLegalClient = legalClients.find(
                      legalClient => selectedValue === legalClient.idLegalClient
                    );

                    if (testLegalClient === undefined) {
                      return 'FEHLER!';
                    } else {
                      return `${testLegalClient.FirstName} ${testLegalClient.LastName}`;
                    }
                  })
                  .join(', ')
              }
              size='small'
              fullWidth
            >
              {legalClients.map(legalclient => (
                <MenuItem
                  key={`legalclient-invoice-${legalclient.idLegalClient}`}
                  value={legalclient.idLegalClient}
                >
                  <Checkbox
                    checked={invoice.LegalClients.includes(
                      legalclient.idLegalClient
                    )}
                  />
                  <ListItemText
                    primary={`${legalclient.FirstName} ${legalclient.LastName}`}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </TableCell>
        <TableCell>
          <CustomeTextField
            attr='Total'
            label='Summe'
            object={invoice}
            setObject={setInvoice}
            type='euro'
          />
        </TableCell>
        <TableCell>{localDocumentObject.FileName}</TableCell>
        <TableCell>
          <Tooltip title='Seitenansicht öffnen'>
            <IconButton
              onClick={async () => {
                if (localDocumentObject.Data) {
                  setSelectedSidepanelDocument(localDocumentObject);
                } else {
                  setSelectedSidepanelDocument(localDocumentObject);

                  getFetch(
                    '/document/',
                    localDocumentObject.idDocument,
                    (localDocumentObject: IDocument) => {
                      setLocalDocumentObject(localDocumentObject);
                      setSelectedSidepanelDocument(localDocumentObject);
                    }
                  );
                }
              }}
            >
              <VerticalSplit />
            </IconButton>
          </Tooltip>
          <Tooltip title='Vorschau'>
            <IconButton onClick={handleOpenPreview}>
              <FindInPage />
            </IconButton>
          </Tooltip>
          {localDocumentObject.idDocument > 0 && downloadedArray && (
            <Tooltip title='Anzahl Downloads'>
              <IconButton onClick={() => setIsOpenDownloaded(true)}>
                <Search />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title='Datei herunterladen'>
            <IconButton onClick={handleDownload}>
              <Download />
            </IconButton>
          </Tooltip>

          {localDocumentObject.idDocument < 0 && (
            <Tooltip title='Datei löschen'>
              <IconButton onClick={handleRemove}>
                <Delete />
              </IconButton>
            </Tooltip>
          )}
        </TableCell>
      </TableRow>
    </>
  );
};
